<template>
    <div class="group flex items-stretch" :class="wrapperClassName">
        <div class="text-gray-500 flex items-stretch" v-if="contentBefore">
            <slot name="before">
                <span class="self-center ml-3" v-text="before" />
            </slot>
        </div>
        <div class="relative grow">
            <raw-decimal-input
                v-bind="$attrs"
                :id="id"
                :value="modelValue"
                :precision="precision"
                :decimal-symbol="defaultDecimalSymbol"
                :thousands-separator="defaultThousandsSeparator"
                :allow-negative-value="allowNegativeValue"
                :disabled="disabled"
                :readonly="readonly"
                :input-class-name="internalInputClassName"
                :format-locale="numberFormatLocale"
                ref="input"
                @focus="onFocus"
                @blur="onBlur"
                @input="onInput"
                @click="$emit('click')"
            />
        </div>
        <div class="text-gray-500 flex items-stretch" v-if="contentAfter">
            <div
                class="self-center relative h-4 w-4 mr-3"
                v-tooltip="errorMessage"
                v-if="!!errorMessage"
            >
                <span
                    class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-50"
                />
                <alert-circle class="h-4 w-4 text-red-500" />
            </div>
            <slot name="after" v-else>
                <span class="self-center mr-3" v-text="after" />
            </slot>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref, useAttrs, useSlots } from 'vue';
    import { useI18n } from '@/hooks/useI18n.ts';
    import AlertCircle from '@/components/activix-ui/assets/alert-circle.svg';
    import { VueClass } from '@/types/Type.ts';
    import { hasSlotContent } from '@/utils/vue-related.ts';
    import RawDecimalInput from './RawDecimalInput.vue';

    defineOptions({ compatConfig: { COMPONENT_V_MODEL: false } });

    const { isFrLocale } = useI18n();

    const props = withDefaults(
        defineProps<{
            allowNegativeValue?: boolean;
            after?: string;
            before?: string;
            decimalSymbol?: string | null;
            disabled?: boolean;
            errorMessage?: string;
            id?: string;
            inputClassName?: VueClass;
            invisible?: boolean;
            paddingClassName?: VueClass;
            precision?: number;
            readonly?: boolean;
            thousandsSeparator?: string | null;
            modelValue?: any;
        }>(),
        {
            allowNegativeValue: false,
            after: '',
            before: '',
            decimalSymbol: null,
            disabled: false,
            errorMessage: '',
            id: '',
            inputClassName: '',
            invisible: false,
            paddingClassName: 'py-2 px-3',
            precision: 2,
            readonly: false,
            thousandsSeparator: null,
            modelValue: '',
        }
    );

    const emit = defineEmits<{
        (e: 'click'): void;
        (e: 'focus', event: FocusEvent): void;
        (e: 'blur', event: FocusEvent): void;
        (e: 'update:modelValue', value: number | null): void;
        (e: 'change', value: number | null): void;
    }>();

    useAttrs(); // keep attrs binding in template
    const slots = useSlots();
    const input = ref<InstanceType<typeof RawDecimalInput> | null>(null);
    const isFocused = ref(false);

    const defaultDecimalSymbol = computed(
        () => props.decimalSymbol || (isFrLocale.value ? ',' : '.')
    );
    const defaultThousandsSeparator = computed(
        () => props.thousandsSeparator || (isFrLocale.value ? ' ' : ',')
    );
    const numberFormatLocale = computed(() => (isFrLocale.value ? 'fr-CA' : 'en-CA'));

    const contentBefore = computed(() => {
        return !!props.before || hasSlotContent(slots, 'before');
    });

    const contentAfter = computed(() => {
        return !!props.after || !!props.errorMessage || hasSlotContent(slots, 'after');
    });

    const wrapperClassName = computed(() => {
        const focusClassName = props.errorMessage
            ? 'ring-1 ring-red-500 border-red-500'
            : 'ring-1 ring-blue-500 !border-blue-500';

        return [
            'border rounded overflow-hidden',
            'transition-colors duration-150',
            isFocused.value ? focusClassName : 'border-gray-300',
            {
                'border-opacity-0': props.invisible && !isFocused.value,
                'hover:border-opacity-100 focus-within:border-opacity-100': !props.disabled,
            },
        ];
    });

    const internalInputClassName = computed(() => {
        return [
            'block min-w-0 w-full border-0 rounded placeholder-gray-800 placeholder-opacity-30',
            'focus:outline-none focus:ring-0 focus:border-transparent',
            'transition-colors duration-150',
            props.paddingClassName,
            props.inputClassName,
            {
                'bg-transparent': props.invisible,
                'bg-gray-200': props.disabled && !props.invisible,
                'cursor-not-allowed': props.disabled,
            },
        ];
    });

    const onFocus = (event: FocusEvent) => {
        isFocused.value = true;
        emit('focus', event);
    };

    const onBlur = (event: FocusEvent) => {
        isFocused.value = false;
        emit('blur', event);
    };

    const onInput = (value: number | null) => {
        emit('update:modelValue', value);
        emit('change', value);
    };

    defineExpose({
        blur: () => input.value?.blur(),
        focus: () => input.value?.focus(),
    });
</script>
